import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient } from '@services/api'

interface PendingItem {
  batchNo: string
  product: string
  billNo: string
  branch: string
  receivedDate: string
  receivedKg: number
  availableKg: number
  partiallyRepacked: boolean
  packSizesG: number[]
  lossTolerancePct: number
}
interface PendingBill {
  billId: number
  billNo: string
  supplierName: string
  billDate: string
  pendingItems: PendingItem[]
  fullyRepacked: string[]
  directSaleCount: number
}
interface Batch {
  batchNo: string
  product: string
  billNo: string
  branch: string
  receivedDate: string
  receivedKg: number
  availableKg: number
}
interface Session {
  id: number
  sessionNo: string
  product: string
  billNo: string
  batchNos: string[]
  outputs: Array<{ packG: number; packets: number; outputKg: number }>
  takes: Array<{ batchNo: string; kg: number }>
  lossTolerancePct: number
  sessionDate: string
  enteredBy: string
  status: 'Active' | 'Draft'
  createdAt: string
}
interface ValProduct { product: string; predictedKg: number; avgUnitCost: number; tolerancePct: number }
interface LossFlag { id: number; product: string; lossKg: number; valueInr: number; status: 'Flagged' | 'Within tolerance'; date: string }

interface OutputRow { id: string; packG: number; packets: number }
interface TakeRow { include: boolean; kg: number }

const kg3 = (n: number) => `${(Number.isFinite(n) ? n : 0).toFixed(3)} kg`
const kg1 = (n: number) => `${(Number.isFinite(n) ? n : 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`
const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const fmtDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const fmtDay = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }).toUpperCase()
const packLabel = (g: number) => (g >= 1000 ? `${g / 1000} kg` : `${g} g`)
const shortName = (product: string) => product.replace(/\s*\(.*?\)\s*/g, '').replace('Sona Masoori Rice', 'Sona Masoori')
const today = () => new Date().toISOString().slice(0, 10)

export function RepackingEntry() {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<PendingItem | null>(null)
  const [draft, setDraft] = useState<Session | null>(null)
  const [sessionDate, setSessionDate] = useState(today())
  const [outputs, setOutputs] = useState<OutputRow[]>([])
  const [takes, setTakes] = useState<Record<string, TakeRow>>({})
  const [takesTouched, setTakesTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string; link?: string } | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [valProduct, setValProduct] = useState('')
  const [actualKg, setActualKg] = useState('')
  const [valSubmitting, setValSubmitting] = useState(false)
  const [valMsg, setValMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  const { data: pendingData } = useQuery({ queryKey: ['repack-pending'], queryFn: () => apiClient.get<any>('/repacking/pending') })
  const { data: productsData } = useQuery({ queryKey: ['products-list'], queryFn: () => apiClient.get<any>('/products') })
  const tamilOf = (name: string): string | null => (productsData?.data?.items || []).find((p: any) => p.name === name)?.nameTamil || null
  const { data: sessionsData } = useQuery({ queryKey: ['repack-sessions'], queryFn: () => apiClient.get<any>('/repacking/sessions?today=true') })
  const { data: batchesData } = useQuery({
    queryKey: ['repack-batches', selected?.product],
    queryFn: () => apiClient.get<any>(`/repacking/batches?product=${encodeURIComponent(selected!.product)}`),
    enabled: !!selected,
  })
  const { data: valProductsData } = useQuery({ queryKey: ['repack-val-products'], queryFn: () => apiClient.get<any>('/repacking/validation-products') })
  const { data: lossData } = useQuery({ queryKey: ['repack-loss-flags'], queryFn: () => apiClient.get<any>('/repacking/loss-flags') })

  const pendingBills: PendingBill[] = pendingData?.data?.items || []
  const sessions: Session[] = sessionsData?.data?.items || []
  const batches: Batch[] = batchesData?.data?.items || []
  const valProducts: ValProduct[] = valProductsData?.data?.items || []
  const lossFlags: LossFlag[] = lossData?.data?.items || []
  const fgByPack: Array<{ packG: number; onHand: number; reorderPoint: number; status: 'OK' | 'Low'; shortfall: number }> = batchesData?.data?.fgByPack || []

  // Deep link from Stock for Sale / bill detail: ?product=… preselects that product's pending item.
  useEffect(() => {
    const preset = searchParams.get('product')
    if (!preset || pendingBills.length === 0) return
    const item = pendingBills.flatMap((b) => b.pendingItems).find((it) => it.product.toLowerCase() === preset.toLowerCase())
    setSearchParams({}, { replace: true })
    if (item) selectItem(item)
    else setMsg({ kind: 'error', text: `No raw stock of ${preset} is waiting for repacking — record a purchase bill first.` })
  }, [searchParams, pendingBills])

  const tolerance = selected?.lossTolerancePct ?? 0
  const totalOutputKg = useMemo(() => outputs.reduce((s, o) => s + ((o.packG || 0) * (o.packets || 0)) / 1000, 0), [outputs])
  const requiredKg = tolerance > 0 ? totalOutputKg / (1 - tolerance / 100) : totalOutputKg
  const totalTakeKg = Object.values(takes).reduce((s, t) => s + (t.include && t.kg > 0 ? t.kg : 0), 0)

  // FIFO suggestion — oldest batches first, until the predicted requirement is covered.
  useEffect(() => {
    if (!selected || takesTouched || batches.length === 0) return
    let remaining = requiredKg
    const next: Record<string, TakeRow> = {}
    batches.forEach((b) => {
      const take = Math.min(b.availableKg, Math.max(remaining, 0))
      const isSelected = b.batchNo === selected.batchNo
      next[b.batchNo] = { include: take > 0 || isSelected, kg: Math.round((isSelected && take <= 0 ? Math.min(b.availableKg, requiredKg || b.availableKg) : take) * 1000) / 1000 }
      remaining -= take
    })
    setTakes(next)
  }, [selected, batches, requiredKg, takesTouched])

  const selectItem = (item: PendingItem) => {
    setSelected(item)
    setDraft(null)
    setOutputs(item.packSizesG.map((g, i) => ({ id: String(i + 1), packG: g, packets: NaN })))
    setTakes({})
    setTakesTouched(false)
    setMsg(null)
    document.getElementById('repack-form')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  const resetForm = () => {
    setSelected(null)
    setDraft(null)
    setOutputs([])
    setTakes({})
    setTakesTouched(false)
    setSessionDate(today())
  }

  const loadDraft = (s: Session) => {
    const allItems = pendingBills.flatMap((b) => b.pendingItems)
    const item =
      allItems.find((it) => s.batchNos.includes(it.batchNo)) ||
      allItems.find((it) => it.product === s.product) || {
        batchNo: s.batchNos[0] || '',
        product: s.product,
        billNo: s.billNo,
        branch: 'Main Repacking Unit',
        receivedDate: s.sessionDate,
        receivedKg: 0,
        availableKg: 0,
        partiallyRepacked: false,
        packSizesG: s.outputs.map((o) => o.packG),
        lossTolerancePct: s.lossTolerancePct,
      }
    setSelected(item)
    setDraft(s)
    setSessionDate(s.sessionDate)
    setOutputs(s.outputs.map((o, i) => ({ id: String(i + 1), packG: o.packG, packets: o.packets })))
    const t: Record<string, TakeRow> = {}
    s.takes.forEach((tk) => { t[tk.batchNo] = { include: true, kg: tk.kg } })
    setTakes(t)
    setTakesTouched(true)
    setMsg(null)
    document.getElementById('repack-form')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  const quickSubmitDraft = async (s: Session) => {
    setMsg(null)
    try {
      const res = await apiClient.post<any>(`/repacking/sessions/${s.id}/submit`)
      setMsg({ kind: 'success', text: res?.message || `${s.sessionNo} submitted`, link: '/dashboard/stock' })
      if (draft?.id === s.id) resetForm()
      refreshAll()
      setTimeout(() => setMsg(null), 6000)
    } catch (e: any) {
      setMsg({ kind: 'error', text: e.response?.data?.message || `Failed to submit ${s.sessionNo}` })
    }
  }

  const discardDraft = async (s: Session) => {
    if (!window.confirm(`Discard draft ${s.sessionNo} (${shortName(s.product)})? This cannot be undone.`)) return
    setMsg(null)
    try {
      const res = await apiClient.delete<any>(`/repacking/sessions/${s.id}`)
      setMsg({ kind: 'success', text: res?.message || `${s.sessionNo} discarded` })
      if (draft?.id === s.id) resetForm()
      refreshAll()
      setTimeout(() => setMsg(null), 6000)
    } catch (e: any) {
      setMsg({ kind: 'error', text: e.response?.data?.message || `Failed to discard ${s.sessionNo}` })
    }
  }

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['repack-pending'] })
    qc.invalidateQueries({ queryKey: ['repack-sessions'] })
    qc.invalidateQueries({ queryKey: ['repack-batches'] })
    qc.invalidateQueries({ queryKey: ['repack-val-products'] })
    qc.invalidateQueries({ queryKey: ['repack-loss-flags'] })
    qc.invalidateQueries({ queryKey: ['dashboard-home'] })
  }

  const submit = async (status: 'Active' | 'Draft') => {
    if (!selected) return setMsg({ kind: 'error', text: 'Select an item from the pending list first' })
    const validOutputs = outputs.filter((o) => o.packG > 0 && o.packets > 0)
    if (validOutputs.length === 0) return setMsg({ kind: 'error', text: 'Enter the packets produced for at least one pack size' })
    const takeList = Object.entries(takes).filter(([, t]) => t.include && t.kg > 0).map(([batchNo, t]) => ({ batchNo, kg: t.kg }))
    if (takeList.length === 0) return setMsg({ kind: 'error', text: 'Tick at least one source batch and enter the kg taken' })
    for (const t of takeList) {
      const b = batches.find((x) => x.batchNo === t.batchNo)
      if (b && t.kg > b.availableKg + 0.0005) return setMsg({ kind: 'error', text: `Batch ${t.batchNo} only has ${b.availableKg} kg available` })
    }
    if (status === 'Active' && totalTakeKg + 0.0005 < requiredKg) {
      return setMsg({ kind: 'error', text: `Source batches cover ${totalTakeKg.toFixed(3)} kg but ${requiredKg.toFixed(3)} kg is required — increase the take or add another batch` })
    }
    setSubmitting(true)
    setMsg(null)
    try {
      const res = await apiClient.post<any>('/repacking/sessions', {
        product: selected.product,
        sessionDate,
        status,
        draftId: draft?.id ?? null,
        outputs: validOutputs.map((o) => ({ packG: o.packG, packets: o.packets })),
        takes: takeList,
      })
      setMsg({ kind: 'success', text: res?.message || 'Repacking entry submitted', link: status === 'Active' ? '/dashboard/stock' : undefined })
      resetForm()
      refreshAll()
      setTimeout(() => setMsg(null), 8000)
    } catch (e: any) {
      setMsg({ kind: 'error', text: e.response?.data?.message || 'Failed to submit repacking entry' })
    } finally {
      setSubmitting(false)
    }
  }

  const valInfo = valProducts.find((p) => p.product === valProduct)
  useEffect(() => {
    if (!valProduct && valProducts.length > 0) setValProduct(valProducts[0].product)
  }, [valProducts, valProduct])
  const actual = Number(actualKg)
  const lossKg = valInfo && actualKg !== '' && Number.isFinite(actual) ? Math.round((valInfo.predictedKg - actual) * 1000) / 1000 : null
  const allowedKg = valInfo ? (valInfo.predictedKg * valInfo.tolerancePct) / 100 : 0
  const lossValue = valInfo && lossKg !== null ? Math.max(lossKg, 0) * valInfo.avgUnitCost : 0
  const lossFlagged = lossKg !== null && lossKg > allowedKg + 0.0005

  const submitValidation = async () => {
    if (!valInfo) return setValMsg({ kind: 'error', text: 'Select a product with raw stock' })
    if (actualKg === '' || !(actual >= 0)) return setValMsg({ kind: 'error', text: 'Enter the actual counted quantity in kg' })
    setValSubmitting(true)
    setValMsg(null)
    try {
      const res = await apiClient.post<any>('/repacking/validation', { product: valProduct, actualKg: actual })
      setValMsg({ kind: 'success', text: res?.message || 'Validation recorded' })
      setActualKg('')
      refreshAll()
      setTimeout(() => setValMsg(null), 6000)
    } catch (e: any) {
      setValMsg({ kind: 'error', text: e.response?.data?.message || 'Failed to record validation' })
    } finally {
      setValSubmitting(false)
    }
  }

  const billMeta = (b: PendingBill) => {
    const n = b.pendingItems.length
    const extras: string[] = []
    if (b.directSaleCount > 0) extras.push(`${b.directSaleCount} direct-sale item${b.directSaleCount === 1 ? '' : 's'} not shown`)
    if (b.fullyRepacked.length > 0) extras.push(`${b.fullyRepacked.map(shortName).join(', ')} already fully repacked, no longer listed`)
    return `${fmtDate(b.billDate)} · ${n} item${n === 1 ? '' : 's'} pending repacking${extras.length ? ` (${extras.join(' · ')})` : ''}`
  }

  const itemMeta = (it: PendingItem) =>
    it.partiallyRepacked
      ? `Batch ${it.batchNo} · ${it.branch} · ${kg1(it.availableKg)} of ${kg1(it.receivedKg)} still available — partially repacked, stays listed`
      : `Batch ${it.batchNo} · ${it.branch} · ${kg1(it.availableKg)} available`

  return (
    <div>
      <div className="hdr">
        <h1>Repacking Entry</h1>
        <p>
          Bills are listed with only their repacking-route items — direct-sale items (biscuits, soap, etc.) don't appear here. Click a bill,
          then an item, to repack it.
        </p>
      </div>

      {msg && (
        <div className={`alert ${msg.kind === 'success' ? 'alert-success' : 'alert-error'}`}>
          {msg.text}
          {msg.link && (
            <>
              {' '}
              <Link to={msg.link} className="underline">
                View Stock for Sale →
              </Link>
            </>
          )}
        </div>
      )}

      <div className="card mb-[18px]">
        <h2>Purchase Bills — Items Pending Repacking</h2>
        {pendingBills.length === 0 && <div className="text-center py-6 text-ink-soft text-[13px]">Nothing waiting — every repacking item has been processed.</div>}
        {pendingBills.map((b, idx) => (
          <details key={b.billId} className="bill-acc" open={idx === 0 || b.pendingItems.some((it) => it.batchNo === selected?.batchNo)}>
            <summary>
              <div>
                <b>
                  Bill {b.billNo} — {b.supplierName}
                </b>
                <div className="bmeta">{billMeta(b)}</div>
              </div>
              <span className="badge badge-pending">{b.pendingItems.length} pending</span>
            </summary>
            <div className="bill-items">
              {b.pendingItems.map((it) => (
                <div key={it.batchNo} className={`item-row ${selected?.batchNo === it.batchNo ? 'selected' : ''}`} onClick={() => selectItem(it)}>
                  <div>
                    <b>{it.product}</b>{tamilOf(it.product) && <span className="text-ink-soft"> · {tamilOf(it.product)}</span>}
                    <div className="imeta">{itemMeta(it)}</div>
                  </div>
                  <span className="btn-ghost btn-sm">Repack →</span>
                </div>
              ))}
            </div>
          </details>
        ))}
        <div className="hint">
          An item disappears once its batch is fully repacked — but the bill stays listed until every item on it is done. This is your
          single view of everything still waiting.
        </div>
      </div>

      <div className="grid2" id="repack-form">
        <div className="card">
          <h2>
            Repacking Entry{' '}
            <small>
              {draft
                ? `— editing draft ${draft.sessionNo} · Batch ${draft.batchNos.join(', ')}`
                : selected
                  ? `— repacking from Batch ${selected.batchNo}`
                  : '— select an item above to begin'}
            </small>
          </h2>
          <div className="row2">
            <div>
              <label>Product</label>
              <input value={selected?.product || ''} placeholder="Select an item above" disabled />
            </div>
            <div>
              <label>Session Date</label>
              <input type="date" value={sessionDate} onChange={(e) => setSessionDate(e.target.value)} />
            </div>
          </div>

          <label className="mt-[14px]">Output Packets Produced</label>
          <table>
            <thead>
              <tr>
                <th>Pack Size</th>
                <th className="num">Packets</th>
                <th className="num">Output (kg)</th>
                <th className="num">On hand / Reorder</th>
                <th className="w-6"></th>
              </tr>
            </thead>
            <tbody>
              {outputs.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-ink-soft">
                    Pack sizes appear here once you pick an item to repack.
                  </td>
                </tr>
              )}
              {outputs.map((o) => (
                <tr key={o.id}>
                  <td>
                    <div className="inline-flex items-center gap-1">
                      <input
                        type="number"
                        min={1}
                        value={Number.isNaN(o.packG) ? '' : o.packG}
                        onChange={(e) => setOutputs((rows) => rows.map((r) => (r.id === o.id ? { ...r, packG: parseFloat(e.target.value) } : r)))}
                        className="!w-20 !py-[6px] text-right"
                      />
                      <span className="text-ink-soft text-xs">g</span>
                      <span className="text-ink-soft text-[11.5px] ml-1">{o.packG > 0 ? `(${packLabel(o.packG)})` : ''}</span>
                    </div>
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      min={0}
                      value={Number.isNaN(o.packets) ? '' : o.packets}
                      onChange={(e) => setOutputs((rows) => rows.map((r) => (r.id === o.id ? { ...r, packets: parseFloat(e.target.value) } : r)))}
                      className="!w-24 !py-[6px] text-right"
                      disabled={!selected}
                    />
                  </td>
                  <td className="num">{(((o.packG || 0) * (o.packets || 0)) / 1000).toFixed(3)}</td>
                  <td className="num text-[12px]" data-testid="onhand-cell">
                    {(() => {
                      const fp = fgByPack.find((p) => p.packG === o.packG)
                      if (!fp) return <span className="text-ink-soft">—</span>
                      return (
                        <>
                          {fp.onHand.toLocaleString('en-IN')} / {fp.reorderPoint.toLocaleString('en-IN')}{' '}
                          <span className={`badge ${fp.status === 'Low' ? 'badge-low' : 'badge-ok'}`}>{fp.status}</span>
                          {fp.status === 'Low' && <div className="text-[10.5px] text-ink-soft">short {fp.shortfall.toLocaleString('en-IN')}</div>}
                        </>
                      )
                    })()}
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="text-ink-soft hover:text-danger disabled:opacity-30 text-base leading-none"
                      disabled={outputs.length === 1}
                      onClick={() => setOutputs((rows) => rows.filter((r) => r.id !== o.id))}
                      title="Remove pack size"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            type="button"
            className="btn-ghost mt-[10px]"
            disabled={!selected}
            onClick={() => setOutputs((rows) => [...rows, { id: String(Math.max(0, ...rows.map((r) => parseInt(r.id))) + 1), packG: NaN, packets: NaN }])}
          >
            + Add another pack size
          </button>

          <div className="card !bg-primary-soft !border-primary-soft mt-[18px]">
            <div className="text-[12px] text-primary-500 font-semibold mb-2">System Prediction (auto-calculated)</div>
            <table className="text-[12.5px]">
              <tbody>
                <tr>
                  <td>Total output</td>
                  <td className="num">{kg3(totalOutputKg)}</td>
                </tr>
                <tr>
                  <td>Expected loss tolerance{selected ? ` (${shortName(selected.product)})` : ''}</td>
                  <td className="num">
                    {tolerance.toFixed(2)}% {tolerance === 0 ? '(default)' : ''}
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>Predicted raw material required</b>
                  </td>
                  <td className="num">
                    <b>{kg3(requiredKg)}</b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="field-label-row mt-4">
            <label className="!m-0">
              Source Batch(es) <small>— confirm which batch this is drawn from</small>
            </label>
          </div>
          <table className="mt-[5px]">
            <thead>
              <tr>
                <th></th>
                <th>Batch</th>
                <th>Branch</th>
                <th className="num">Received</th>
                <th className="num">Available</th>
                <th className="num">Take (kg)</th>
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-ink-soft">
                    {selected ? 'No batches with available stock for this product.' : 'Available batches are listed here once you pick an item.'}
                  </td>
                </tr>
              )}
              {batches.map((b, i) => {
                const t = takes[b.batchNo] || { include: false, kg: NaN }
                return (
                  <tr key={b.batchNo}>
                    <td>
                      <input
                        type="checkbox"
                        checked={t.include}
                        onChange={(e) => {
                          setTakesTouched(true)
                          setTakes((prev) => ({ ...prev, [b.batchNo]: { include: e.target.checked, kg: e.target.checked && !(t.kg > 0) ? b.availableKg : t.kg } }))
                        }}
                      />
                    </td>
                    <td>
                      {b.batchNo}
                      {i === 0 && <span className="badge badge-ok ml-1">Oldest</span>}
                    </td>
                    <td>{b.branch}</td>
                    <td className="num">{fmtDay(b.receivedDate)}</td>
                    <td className="num">{kg1(b.availableKg)}</td>
                    <td className="num">
                      <input
                        type="number"
                        min={0}
                        step="0.1"
                        value={Number.isFinite(t.kg) ? t.kg : ''}
                        disabled={!t.include}
                        onChange={(e) => {
                          setTakesTouched(true)
                          setTakes((prev) => ({ ...prev, [b.batchNo]: { include: true, kg: parseFloat(e.target.value) } }))
                        }}
                        className={`!w-24 !py-[5px] text-right ${t.include && t.kg > b.availableKg + 0.0005 ? '!border-danger' : ''}`}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {selected && (
            <div className={`hint ${totalTakeKg + 0.0005 >= requiredKg ? 'calc' : ''}`}>
              {takesTouched
                ? `Selected batches cover ${totalTakeKg.toFixed(3)} kg of the ${requiredKg.toFixed(3)} kg required.`
                : 'System suggested oldest-first (FIFO) to cover the predicted requirement — adjust the split above if needed.'}
              {totalTakeKg + 0.0005 < requiredKg && requiredKg > 0 && (
                <span className="text-danger"> Short by {(requiredKg - totalTakeKg).toFixed(3)} kg — increase a take or tick another batch.</span>
              )}
            </div>
          )}

          <div className="btnrow">
            <button type="button" className="btn-primary" disabled={!selected || submitting} onClick={() => submit('Active')}>
              {submitting ? 'Submitting…' : draft ? `Submit ${draft.sessionNo}` : 'Submit Repacking Entry'}
            </button>
            <button type="button" className="btn-ghost" disabled={!selected || submitting} onClick={() => submit('Draft')}>
              {draft ? 'Update Draft' : 'Save as Draft'}
            </button>
            {draft && (
              <button type="button" className="btn-ghost" disabled={submitting} onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
          <div className="hint">Single entry, no separate approval step — stock is deducted and finished goods created immediately on submit.</div>
        </div>

        <div className="card">
          <h2>Today's Repacking Sessions</h2>
          {sessions.length === 0 && <div className="text-center py-6 text-ink-soft text-[13px]">No repacking sessions entered today yet.</div>}
          {sessions.map((s) => (
            <div key={s.id} className={`queue-item ${draft?.id === s.id ? '!bg-primary-soft !border-primary-500' : ''}`}>
              <div>
                <b>{shortName(s.product)}</b> — {s.outputs.map((o) => `${packLabel(o.packG).replace(' ', '')} × ${o.packets.toLocaleString('en-IN')}`).join(', ')}
                <div className="qmeta">
                  {s.sessionNo} · Bill {s.billNo} · Batch {s.batchNos.join(', ')} · Entered by {s.enteredBy} · {fmtTime(s.createdAt)}
                </div>
                {s.status === 'Draft' && (
                  <div className="flex gap-2 mt-2">
                    <button type="button" className="btn-ghost btn-xs" onClick={() => loadDraft(s)}>
                      Edit &amp; Submit
                    </button>
                    <button type="button" className="btn-accent btn-xs" onClick={() => quickSubmitDraft(s)}>
                      Submit as is
                    </button>
                    <button type="button" className="btn-danger btn-xs" onClick={() => discardDraft(s)}>
                      Discard
                    </button>
                  </div>
                )}
              </div>
              <span className={`badge ${s.status === 'Draft' ? 'badge-draft' : 'badge-full'}`}>{s.status}</span>
            </div>
          ))}
          {sessions.some((s) => s.status === 'Draft') && (
            <div className="hint">Drafts haven't touched stock. Edit &amp; Submit loads the draft into the form; Submit as is posts it unchanged.</div>
          )}
        </div>
      </div>

      <div className="an-grid2">
        <div className="card">
          <h2>Physical Stock Validation</h2>
          {valMsg && <div className={`alert ${valMsg.kind === 'success' ? 'alert-success' : 'alert-error'}`}>{valMsg.text}</div>}
          <label>Product</label>
          <select value={valProduct} onChange={(e) => { setValProduct(e.target.value); setActualKg('') }}>
            {valProducts.length === 0 && <option value="">No raw stock to validate</option>}
            {valProducts.map((p) => (
              <option key={p.product} value={p.product}>
                {p.product}
              </option>
            ))}
          </select>
          <div className="row2 mt-[14px]">
            <div>
              <label>System predicted balance</label>
              <input value={valInfo ? kg1(valInfo.predictedKg) : ''} disabled />
            </div>
            <div>
              <label>Actual counted (kg)</label>
              <input type="number" min={0} step="0.1" value={actualKg} onChange={(e) => setActualKg(e.target.value)} placeholder="e.g. 19.8" />
            </div>
          </div>
          {valInfo && lossKg !== null && (
            <div className={lossFlagged ? 'hint-error' : 'hint calc'}>
              {lossKg > 0
                ? `→ Loss detected: ${lossKg} kg (≈ ${inr(lossValue)} at current cost) — ${lossFlagged ? `flagged since ${valInfo.tolerancePct === 0 ? 'default tolerance is 0%' : `tolerance is ${valInfo.tolerancePct}%${allowedKg >= 0.0005 ? ` (${allowedKg.toFixed(3)} kg)` : ''}`}` : `within the ${valInfo.tolerancePct}% tolerance`}.`
                : lossKg < 0
                  ? `→ Counted ${Math.abs(lossKg)} kg more than the system balance — raw stock will be adjusted up.`
                  : '→ Physical count matches the system balance.'}
            </div>
          )}
          <div className="btnrow">
            <button type="button" className="btn-accent" disabled={!valInfo || valSubmitting} onClick={submitValidation}>
              {valSubmitting ? 'Submitting…' : 'Submit Validation'}
            </button>
          </div>
        </div>
        <div className="card">
          <h2>Recent Loss Flags</h2>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th className="num">Loss</th>
                <th className="num">Value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {lossFlags.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-ink-soft">
                    No validations recorded yet.
                  </td>
                </tr>
              )}
              {lossFlags.map((f) => (
                <tr key={f.id}>
                  <td>{shortName(f.product)}</td>
                  <td className="num">{f.lossKg < 0 ? `+${kg1(Math.abs(f.lossKg))} gain` : kg1(f.lossKg)}</td>
                  <td className="num">{f.lossKg < 0 ? '—' : inr(f.valueInr)}</td>
                  <td>
                    <span className={`badge ${f.status === 'Flagged' ? 'badge-loss' : 'badge-ok'}`}>{f.lossKg < 0 ? 'Gain' : f.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
